import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, ShoppingBag, Menu, X, ChevronDown, Heart, Briefcase } from "lucide-react";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useLocale } from "@/context/LocaleContext";
import { useB2B } from "@/context/B2BContext";
import { LanguageCurrencySwitcher } from "@/components/LanguageCurrencySwitcher";
import { getSharedPublic } from "@/lib/api";
import { cn } from "@/lib/utils";
import { resolveField } from "@/theme/sectionDefs";
import useThemeConfig from "@/theme/useThemeConfig";

const DEFAULT_HEADER_SETTINGS = {
  logo_url: "/brand/levami-logo.png",
  logo_height_desktop: 65,
  logo_height_mobile: 50,
  header_height_desktop: 100,
  header_height_mobile: 78,
  transparent_header_on_hero: true,
};

const SHOP_COLUMNS = [
  { key: "nouveautes", to: "/boutique/nouveautes" },
  { key: "keffiehs", to: "/boutique/keffiehs" },
  { key: "robes_abayas", to: "/boutique/robes-abayas" },
  { key: "pieces_brodees", to: "/boutique/pieces-brodees" },
  { key: "chales_ponchos", to: "/boutique/chales-ponchos" },
  { key: "sacs_accessoires", to: "/boutique/sacs-accessoires" },
  { key: "bijoux", to: "/boutique/bijoux" },
];

/* Découvrir + secondary nav links now come from the Theme Editor (/admin/theme
   > En-tête), fully reorderable/editable there. This default mirrors the
   backend's DEFAULT_THEME so the header renders identically before the theme
   config has loaded. */
const DEFAULT_NAV_ITEMS = [
  { id: "nav-boutique", type: "fixed_megamenu", locked: true, label: "", url: "" },
  { id: "nav-histoire", type: "link", label: "", url: "/histoire" },
  { id: "nav-artisanat", type: "link", label: "", url: "/artisanat" },
  { id: "nav-lookbook", type: "link", label: "", url: "/lookbook" },
  { id: "nav-contact", type: "link", label: "", url: "/contact" },
  {
    id: "nav-decouvrir", type: "dropdown", label: "",
    children: [
      { id: "nav-decouvrir-journal", label: "", url: "/journal" },
      { id: "nav-decouvrir-ateliers", label: "", url: "/ateliers" },
      { id: "nav-decouvrir-partenariats", label: "", url: "/partenariats" },
    ],
  },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [settings, setSettings] = useState(DEFAULT_HEADER_SETTINGS);
  const headerTheme = useThemeConfig("header", { nav_items: DEFAULT_NAV_ITEMS });
  const navItems = headerTheme.nav_items || [];
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { itemCount, setIsOpen } = useCart();
  const { count: wishlistCount } = useWishlist();
  const { t, locale } = useLocale();
  const { account, loading: b2bLoading } = useB2B();

  const isHome = location.pathname === "/";

  useEffect(() => {
    let active = true;
    getSharedPublic("/categories", { lang: locale }).then((res) => { if (active) setCategories(res.data || []); }).catch(() => {});
    return () => { active = false; };
  }, [locale]);

  /* Shop mega-menu groups: "Nouveautés" first, then each main category with its
     sub-categories nested beneath. Falls back to the static links until the API
     responds, so the menu never renders empty. */
  const shopGroups = React.useMemo(() => {
    const mains = categories.filter((c) => !c.parent_id).sort((a, b) => (a.order || 0) - (b.order || 0));
    if (mains.length === 0) {
      return SHOP_COLUMNS.map((col) => ({ slug: col.to.split("/").pop(), to: col.to, label: t(`shop.${col.key}`), children: [] }));
    }
    const groups = [{ slug: "nouveautes", to: "/boutique/nouveautes", label: t("shop.nouveautes"), children: [] }];
    mains.forEach((main) => {
      groups.push({
        slug: main.slug,
        to: `/boutique/${main.slug}`,
        label: main.display_name || main.name,
        children: categories
          .filter((c) => c.parent_id === main.id)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((sub) => ({ slug: sub.slug, to: `/boutique/${sub.slug}`, label: sub.display_name || sub.name })),
      });
    });
    return groups;
  }, [categories, t]);

  useEffect(() => {
    let active = true;
    getSharedPublic("/content/general_settings").then((res) => {
      if (active && res.data?.data) setSettings((s) => ({ ...s, ...res.data.data }));
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const transparent = isHome && settings.transparent_header_on_hero && !scrolled;

  const submitSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/boutique?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery("");
    }
  };

  return (
    <header
      data-testid="site-header"
      style={{
        "--header-h-desktop": `${settings.header_height_desktop}px`,
        "--header-h-mobile": `${settings.header_height_mobile}px`,
        "--logo-h-desktop": `${settings.logo_height_desktop}px`,
        "--logo-h-mobile": `${settings.logo_height_mobile}px`,
      }}
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        transparent ? "bg-transparent" : "bg-levami-ivoire border-b border-levami-sable"
      }`}
    >
      <a href="#main-content" className="skip-link">Aller au contenu</a>
      <div
        className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between h-[var(--header-h-mobile)] sm:h-[var(--header-h-desktop)]"
      >
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="header-logo-link">
          <img
            src={settings.logo_url}
            alt="LEVAMI"
            className="w-auto object-contain h-[var(--logo-h-mobile)] sm:h-[var(--logo-h-desktop)]"
          />
        </Link>

        <nav className="hidden xl:flex items-center">
          <NavigationMenu>
            <NavigationMenuList className="gap-1">
              <NavigationMenuItem>
                <NavigationMenuTrigger
                  data-testid="nav-boutique-mega-menu-trigger"
                  className={`bg-transparent text-sm tracking-wide ${transparent ? "text-levami-charbon" : "text-levami-charbon"} hover:bg-levami-creme data-[state=open]:bg-levami-creme`}
                >
                  {t("nav.boutique")}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="w-[560px] p-6 bg-levami-creme">
                    <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                      {shopGroups.map((group) => (
                        <div key={group.to}>
                          <Link
                            to={group.to}
                            data-testid={`nav-boutique-link-${group.slug}`}
                            className="block text-sm font-medium text-levami-charbon hover:text-levami-primary transition-colors py-1"
                          >
                            {group.label}
                          </Link>
                          {group.children.length > 0 && (
                            <div className="mt-1 space-y-0.5 border-l border-levami-sable pl-3">
                              {group.children.map((sub) => (
                                <Link
                                  key={sub.to}
                                  to={sub.to}
                                  data-testid={`nav-boutique-sublink-${sub.slug}`}
                                  className="block text-[13px] text-levami-brun/80 hover:text-levami-primary transition-colors py-0.5"
                                >
                                  {sub.label}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="mt-5 pt-4 border-t border-levami-sable">
                      <Link to="/boutique" className="text-sm font-medium text-levami-charbon hover:text-levami-primary underline">
                        {t("nav.voir_toute_boutique")}
                      </Link>
                    </div>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>
              {navItems.filter((it) => it.type === "link" || it.type === "dropdown").map((item) => (
                <NavigationMenuItem key={item.id}>
                  {item.type === "dropdown" ? <DropdownMenu>
                    <DropdownMenuTrigger data-testid={`nav-${item.id}-menu-trigger`} className={cn(navigationMenuTriggerStyle(), "bg-transparent text-sm tracking-wide text-levami-charbon hover:bg-levami-creme focus:bg-levami-creme data-[state=open]:bg-levami-creme outline-none")}>
                      {resolveField(item.id, "label", item, t)}<ChevronDown className="relative top-[1px] ml-1 h-3 w-3" aria-hidden="true" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" sideOffset={8} className="w-[220px] p-2 bg-levami-creme border-levami-sable">
                      {(item.children || []).map((link) => <DropdownMenuItem key={link.id} asChild className="cursor-pointer focus:bg-levami-ivoire"><Link to={link.url} data-testid={`nav-decouvrir-link-${link.url.replace(/\//g, "")}`} className="text-sm text-levami-brun hover:text-levami-primary transition-colors py-1.5 px-2 w-full">{resolveField(link.id, "label", link, t)}</Link></DropdownMenuItem>)}
                    </DropdownMenuContent>
                  </DropdownMenu> :
                  <Link
                    to={item.url}
                    data-testid={`nav-link-${item.url.replace(/\//g, "")}`}
                    className="inline-flex h-10 items-center px-5 text-sm tracking-wide text-levami-charbon hover:text-levami-primary transition-colors"
                  >
                    {resolveField(item.id, "label", item, t)}
                  </Link>}
                </NavigationMenuItem>
              ))}
            </NavigationMenuList>
          </NavigationMenu>
          <Link
            to={account ? "/pro/catalogue" : "/pro?tab=register"}
            data-testid="nav-espace-pro-link"
            className="ml-2 inline-flex h-10 items-center gap-1.5 px-4 text-sm tracking-wide text-levami-brun/80 hover:text-levami-primary transition-colors border-l border-levami-sable"
          >
            <Briefcase className="h-3.5 w-3.5" aria-hidden="true" />
            {account ? t("b2b.nav_catalog") : t("nav.espace_pro")}
          </Link>
          {account && (
            <Link
              to="/pro/commandes"
              data-testid="nav-b2b-orders-link"
              className="ml-1 inline-flex h-10 items-center px-3 text-sm text-levami-brun/80 hover:text-levami-primary transition-colors"
            >
              {t("b2b.nav_orders")}
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-1 sm:gap-2">
          <div className="hidden sm:block">
            <LanguageCurrencySwitcher variant="header" />
          </div>

          <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="icon" aria-label={t("nav.search_aria")} data-testid="nav-search-button">
                <Search className="h-5 w-5 text-levami-charbon" />
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-levami-creme border-levami-sable max-w-lg">
              <DialogTitle className="sr-only">{t("nav.search_title")}</DialogTitle>
              <DialogDescription className="sr-only">Saisissez un mot-clé pour rechercher un produit dans la boutique LEVAMI.</DialogDescription>
              <form onSubmit={submitSearch} className="pt-4">
                <label htmlFor="site-search" className="text-sm text-levami-brun mb-2 block">{t("nav.search_label")}</label>
                <div className="flex gap-2">
                  <Input
                    id="site-search"
                    type="search"
                    autoFocus
                    autoComplete="off"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t("nav.search_placeholder")}
                    data-testid="search-modal-input"
                    className="border-levami-sable"
                  />
                  <Button type="submit" className="bg-levami-charbon text-levami-creme hover:bg-levami-brun" data-testid="search-modal-submit">
                    {t("nav.search_button")}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {!account && !b2bLoading && (
            <>
              <Button
                variant="ghost"
                size="icon"
                asChild
                aria-label={t("wishlist.nav_aria")}
                data-testid="nav-wishlist-button"
                className="relative"
              >
                <Link to="/favoris">
                  <Heart className="h-5 w-5 text-levami-charbon" />
                  {wishlistCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-levami-primary text-levami-creme text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                      {wishlistCount}
                    </span>
                  )}
                </Link>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("nav.cart_aria")}
                onClick={() => setIsOpen(true)}
                data-testid="nav-cart-button"
                className="relative"
              >
                <ShoppingBag className="h-5 w-5 text-levami-charbon" />
                {itemCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-levami-primary text-levami-creme text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </Button>
            </>
          )}

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="xl:hidden" aria-label={t("nav.menu_aria")} data-testid="mobile-menu-button">
                <Menu className="h-5 w-5 text-levami-charbon" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-levami-creme border-levami-sable w-[85vw] sm:max-w-sm overflow-y-auto">
              <SheetTitle className="sr-only">{t("nav.menu_title")}</SheetTitle>
              <div className="mt-6">
                <LanguageCurrencySwitcher variant="mobile" />
              </div>
              <nav className="flex flex-col gap-1 mt-6" data-testid="mobile-nav-menu">
                <SheetClose asChild>
                  <Link to="/" className="px-2 py-2.5 text-base font-medium text-levami-charbon hover:text-levami-primary" data-testid="mobile-nav-accueil-link">
                    {t("nav.accueil")}
                  </Link>
                </SheetClose>
                <div className="h-px bg-levami-sable my-2" />
                <p className="text-xs tracking-label uppercase text-levami-brun/60 px-2 mb-1">{t("nav.boutique")}</p>
                {shopGroups.map((group) => (
                  <div key={group.to}>
                    <SheetClose asChild>
                      <Link to={group.to} className="block px-2 py-2.5 text-base text-levami-charbon hover:text-levami-primary">
                        {group.label}
                      </Link>
                    </SheetClose>
                    {group.children.length > 0 && (
                      <div className="ml-4 border-l border-levami-sable pl-3">
                        {group.children.map((sub) => (
                          <SheetClose asChild key={sub.to}>
                            <Link to={sub.to} className="block px-2 py-2 text-sm text-levami-brun/80 hover:text-levami-primary">
                              {sub.label}
                            </Link>
                          </SheetClose>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <SheetClose asChild>
                  <Link to="/boutique" className="px-2 py-2.5 text-base font-medium text-levami-charbon underline">
                    {t("nav.voir_toute_boutique")}
                  </Link>
                </SheetClose>
                <div className="h-px bg-levami-sable my-3" />
                {navItems.filter((it) => it.type === "link" || it.type === "dropdown").map((item) => item.type === "link" ? (
                  <SheetClose asChild key={item.id}>
                    <Link to={item.url} className="block px-2 py-2.5 text-base text-levami-charbon hover:text-levami-primary">
                      {resolveField(item.id, "label", item, t)}
                    </Link>
                  </SheetClose>
                ) : (
                  <div key={item.id}>
                    <div className="h-px bg-levami-sable my-3" />
                    <p className="text-xs tracking-label uppercase text-levami-brun/60 px-2 mb-1">{resolveField(item.id, "label", item, t)}</p>
                    {(item.children || []).map((link) => (
                      <SheetClose asChild key={link.id}>
                        <Link to={link.url} className="block px-2 py-2.5 text-base text-levami-charbon hover:text-levami-primary">
                          {resolveField(link.id, "label", link, t)}
                        </Link>
                      </SheetClose>
                    ))}
                  </div>
                ))}
                <div className="h-px bg-levami-sable my-3" />
                {account && (
                  <>
                    <SheetClose asChild>
                      <Link to="/pro/catalogue" className="px-2 py-2.5 text-base text-levami-charbon hover:text-levami-primary">
                        {t("b2b.nav_catalog")}
                      </Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <Link to="/pro/commandes" className="px-2 py-2.5 text-base text-levami-charbon hover:text-levami-primary">
                        {t("b2b.nav_orders")}
                      </Link>
                    </SheetClose>
                  </>
                )}
                <SheetClose asChild>
                  <Link
                    to={account ? "/pro/catalogue" : "/pro?tab=register"}
                    data-testid="mobile-nav-espace-pro-link"
                    className="px-2 py-2.5 text-base text-levami-charbon hover:text-levami-primary inline-flex items-center gap-2"
                  >
                    <Briefcase className="h-4 w-4 text-levami-brun/70" aria-hidden="true" />
                    {account ? t("b2b.nav_catalog") : t("nav.espace_pro")}
                  </Link>
                </SheetClose>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
